import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

export interface Tip {
  id: number;
  student_id: number;
  partner_id: number;
  amount: number;
  message?: string;
  type?: string;
  task_id?: number;
  status: string;
  created_at: string;
  student_name?: string;
  student_email?: string;
  partner_name?: string;
}

// Demo fallback data
export const DEMO_STUDENT_TIPS: Tip[] = [
  { id: 1, student_id: 1, partner_id: 1, partner_name: "Tanzania Tech Hub", amount: 50, message: "Great work on the dashboard!", type: "appreciation", status: "completed", created_at: "2026-05-10T14:00:00Z" },
  { id: 2, student_id: 1, partner_id: 2, partner_name: "Dar es Salaam University", amount: 100, message: "Excellent research skills!", type: "milestone", status: "completed", created_at: "2026-05-08T10:00:00Z" },
  { id: 3, student_id: 1, partner_id: 3, partner_name: "Kilimanjaro Solutions", amount: 25, message: "Quick turnaround on the report", type: "appreciation", status: "completed", created_at: "2026-05-05T16:00:00Z" },
];

export const DEMO_ADMIN_TIPS: Tip[] = DEMO_STUDENT_TIPS.map((t, i) => ({
  ...t,
  student_name: ["Amina Juma", "John Mwangi", "Grace Kimani"][i] || "Student",
  student_email: [`amina@example.com`, `john@example.com`, `grace@example.com`][i] || "student@example.com",
}));

export const DEMO_PARTNER_SENT_TIPS: Tip[] = [
  { id: 1, student_id: 5, student_name: "Amina Juma", amount: 75, message: "Outstanding presentation skills!", type: "appreciation", status: "completed", created_at: "2026-05-12T09:00:00Z", partner_id: 10 },
  { id: 2, student_id: 8, student_name: "Kofi Mensah", amount: 50, message: "Great teamwork on the project", type: "appreciation", status: "completed", created_at: "2026-05-09T14:00:00Z", partner_id: 10 },
];

export function useStudentTips() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["tips", "my"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_STUDENT_TIPS;
      try {
        const res = await apiClient.get("/students/me/tips");
        return unwrapApiArray<Tip>(res);
      } catch {
        return [];
      }
    },
  });
}

export function usePartnerSentTips() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["tips", "partner"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_PARTNER_SENT_TIPS;
      try {
        const res = await apiClient.get("/partners/me/tips");
        return unwrapApiArray<Tip>(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminTips() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["tips", "admin"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_ADMIN_TIPS;
      try {
        const res = await apiClient.get("/admin/tips");
        return unwrapApiArray<Tip>(res);
      } catch {
        return [];
      }
    },
  });
}

// Create a tip (for partners)
export function useCreateTip() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async ({ studentId, amount, message, taskId }: { studentId: number; amount: number; message?: string; taskId?: number }) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id: Date.now(), student_id: studentId, amount, message, task_id: taskId, status: "completed", created_at: new Date().toISOString() };
      }
      const res = await apiClient.post("/partners/me/tips", {
        student_id: studentId,
        amount,
        message,
        task_id: taskId,
      });
      return unwrapApiObject(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tips"] });
    },
  });
}

// Get my tips (for students)
export function useMyTips() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["tips", "my-tips"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_STUDENT_TIPS;
      try {
        const res = await apiClient.get("/students/me/tips");
        return unwrapApiArray<Tip>(res);
      } catch {
        return [];
      }
    },
  });
}

// Get tips sent by partner
export function usePartnerTips() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["tips", "partner-tips"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_PARTNER_SENT_TIPS;
      try {
        const res = await apiClient.get("/partners/me/tips");
        return unwrapApiArray<Tip>(res);
      } catch {
        return [];
      }
    },
  });
}

// Get wallet balance
export function useWallet() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["wallet", user?.id],
    queryFn: async () => {
      if (user?.isDemo) return { balance: 5000, currency: "TZS" };
      try {
        const res = await apiClient.get("/wallet");
        return unwrapApiObject(res);
      } catch {
        return null;
      }
    },
    enabled: !!user?.id,
  });
}

// Get tips leaderboard (for admin)
export function useLeaderboard() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["leaderboard", "tips"],
    queryFn: async () => {
      if (user?.isDemo) return [];
      try {
        const res = await apiClient.get("/leaderboard/tips");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

// Get hours leaderboard (for admin)
export function useHoursLeaderboard(period: string = "all") {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["leaderboard", "hours", period],
    queryFn: async () => {
      if (user?.isDemo) return [];
      try {
        const res = await apiClient.get(`/admin/leaderboard/hours?period=${period}`);
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export interface Withdrawal {
  id: number;
  amount: number;
  fee?: number;
  payout_amount?: number;
  currency: string;
  phone: string;
  status: "processing" | "completed" | "failed" | "reversed";
  failure_reason?: string;
  created_at: string;
  completed_at?: string;
}

// A student's tip withdrawals. Polls while one is still being sent, so the
// page shows the outcome without a refresh.
export function useMyWithdrawals() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["wallet", "withdrawals", user?.id],
    queryFn: async () => {
      if (user?.isDemo) return [] as Withdrawal[];
      try {
        const res = await apiClient.get("/wallet/withdrawals");
        return unwrapApiArray<Withdrawal>(res);
      } catch {
        return [] as Withdrawal[];
      }
    },
    enabled: !!user?.id,
    refetchInterval: (query) =>
      (query.state.data as Withdrawal[] | undefined)?.some((w) => w.status === "processing") ? 5000 : false,
  });
}

export interface WithdrawalQuote {
  amount: number;
  fee: number;
  payout_amount: number;
}

// Snippe's fee for a withdrawal, which the student pays, and what reaches
// their phone. `amount` should already be debounced by the caller.
export function useWithdrawalQuote(amount: number, enabled: boolean) {
  return useQuery({
    queryKey: ["wallet", "withdraw-quote", amount],
    queryFn: async () => {
      const res = await apiClient.get(`/wallet/withdraw/quote?amount=${amount}`);
      return unwrapApiObject(res) as unknown as WithdrawalQuote;
    },
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

// Withdraw tips to a mobile money number through a Snippe payout. The amount
// is held from the balance at once and released again if the payout fails.
export function useWithdrawTips() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async ({ amount, phone }: { amount: number; phone: string }) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id: Date.now(), amount, phone, status: "completed" } as unknown as Withdrawal;
      }
      const res = await apiClient.post("/wallet/withdraw", { amount, phone });
      return unwrapApiObject(res) as unknown as Withdrawal;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
    },
  });
}
